/**
 * Catalogus-overrides (admin-bewerkbaar, live): een dún laagje bovenop de statische
 * catalogus. Opgeslagen in Firestore `settings/catalog`, één keer per sessie gelezen en
 * gecachet — de zware catalogus blijft statisch, dus de site blijft snel.
 *
 * Omdat de catalogus alleen VERKOOPprijzen bevat (marge is bij de sync al ingebakken),
 * werkt de marge-knop hier als een PRIJSFACTOR op de huidige prijs (1.10 = +10%), naast
 * een vaste €/m²-override per variant en een aan/uit-schakelaar.
 */
export interface CatalogOverrides {
  /** Prijsfactor op de huidige verkoopprijs (1 = ongewijzigd). */
  priceFactor?: number;
  /** Prijsfactor per categorie/soort (sleutel = soort-slug of categorie); wint van globaal. */
  priceFactorPerCategorie?: Record<string, number>;
  /** Prijsfactor per variant/artikel (sleutel = variant-id of artikel-slug); wint van categorie. */
  priceFactorPerSlug?: Record<string, number>;
  /** Vaste €/m²-override per variant (in centen) — wint van álle factoren. */
  priceOverrideCents?: Record<string, number>;
  /** Eigen (klantvriendelijke) productnaam per artikel-slug of soort-slug — vervangt de Timmers-naam. */
  nameOverride?: Record<string, string>;
  /** Eigen artikelnummer per artikel-slug — vervangt het Timmers-nummer op de site. */
  articleNumberOverride?: Record<string, string>;
  /**
   * Profiel-/beslagsysteem per artikel-slug (bv. "70KLIK", "13DFKLIK").
   *
   * We leiden het systeem normaal af uit het leveranciersnummer en uit "t.b.v. …" in de naam,
   * maar die informatie staat er lang niet altijd in. Dit veld is de menselijke correctie: het
   * wint van alles wat we afleiden, en zorgt dat de juiste toebehoren bij het juiste profiel
   * verschijnen — en de verkeerde er niet bij komen.
   */
  systeemPerSlug?: Record<string, string>;
  /** Uitgeschakelde varianten (variant-id) en/of soorten (soort-slug). */
  disabled?: string[];
  /** Afwijkende levertijd-tekst per soort (soort-slug) — overschrijft de globale levertijd. */
  leadTimePerSoort?: Record<string, string>;
  /**
   * Verberg producten/varianten automatisch als hun effectieve prijs €0 is (waarschijnlijk een
   * sync-fout). "Op aanvraag"-artikelen en configureerbare artikelen met een vanaf-prijs blijven
   * staan. De €0-ids worden client-side afgeleid en aan `disabled` toegevoegd (withZeroPricedDisabled).
   */
  hideZeroPriced?: boolean;
  /**
   * Op-lengte-artikelen (U-profielen) die WÉL op maat gezaagd mogen worden (artikel-slug).
   * Standaard (niet in de lijst) = verkoop per HELE handelslengte; de API kent dit onderscheid
   * niet, dus het is een admin-keuze. Alléén relevant voor lengthArticle-artikelen.
   */
  cutToLength?: string[];
  /**
   * Handmatige correctie op de verkoopwijze van een profiel (per slug). De sync leest die uit de
   * Timmers-omschrijving, en die tekst is niet altijd volledig — hiermee zet je het recht zonder
   * te wachten op een nieuwe omschrijving. Alleen ingevulde velden overschrijven.
   */
  profielVerkoopPerSlug?: Record<string, { perM1?: boolean; handelslengteMm?: number; opMaatMogelijk?: boolean }>;
  /**
   * Artikelen die met ons eigen transport gaan in plaats van met de pakketdienst (verstekstansen,
   * vouwwanden, profielen van 5 m). Artikel-slugs, `groep:<Timmers-groep>` of
   * `subgroep:<Timmers-subgroep>` — zie gaatMetEigenTransport in articles/eigen-transport.ts.
   */
  eigenTransport?: string[];
}

/** Mag dit op-lengte-artikel op maat gezaagd worden (admin-keuze)? Anders = hele handelslengte. */
export function isCutToLength(slug: string, ov: CatalogOverrides | null | undefined): boolean {
  return !!ov?.cutToLength?.includes(slug);
}

/** Levertijd voor een soort: de per-soort-override of de globale fallback. */
export function soortLeadTime(soortSlug: string, fallback: string, ov: CatalogOverrides | null | undefined): string {
  const t = ov?.leadTimePerSoort?.[soortSlug];
  return t && t.trim() ? t : fallback;
}

export const EMPTY_OVERRIDES: CatalogOverrides = {};

const clampFactor = (f: number | undefined): number =>
  Number.isFinite(f) && (f as number) > 0 ? Math.min(Math.max(f as number, 0.1), 10) : 1;

/**
 * Effectieve €/m² (centen) voor een variant, gegeven de overrides.
 * Volgorde: vaste override → categorie-factor → globale factor → ongewijzigd.
 * `cats` = mogelijke categorie-sleutels (soort-slug, subcategory, top-categorie) — de
 * eerste die in `priceFactorPerCategorie` voorkomt telt.
 */
export function effectivePricePerM2(
  basePricePerM2: number,
  variantId: string,
  ov: CatalogOverrides | null | undefined,
  cats: string[] = [],
): number {
  if (!ov) return basePricePerM2;
  const fixed = ov.priceOverrideCents?.[variantId];
  if (fixed != null && Number.isFinite(fixed) && fixed >= 0) return Math.round(fixed);
  const perSlug = ov.priceFactorPerSlug?.[variantId];
  const perCat = ov.priceFactorPerCategorie ?? {};
  const catFactor = cats.map((c) => perCat[c]).find((f) => f != null);
  // Volgorde: per-variant-percentage → categorie-factor → globale factor.
  const factor = clampFactor(perSlug ?? catFactor ?? ov.priceFactor);
  return Math.round(basePricePerM2 * factor);
}

/**
 * Alleen de prijs-FACTOR (variant → categorie → globaal), zonder de vaste €/m²-override. Voor
 * prijzen die NIET per m² zijn (bewerkingen, ventilatieroosters op staffel, voorzetramen op
 * matrix): de admin-marge werkt dan als factor over de berekende eindprijs.
 *
 * Dezelfde volgorde als `effectivePricePerM2`, inclusief de marge per variant. Die stond hier
 * eerder niet in, waardoor bewerkingen zónder marge doorgingen zodra de marge per glassoort was
 * ingesteld in plaats van per categorie. (Thijs, 28-07-2026: bewerkingen volgen de glasmarge.)
 */
export function catalogFactor(
  ov: CatalogOverrides | null | undefined,
  cats: string[] = [],
  variantId?: string,
): number {
  if (!ov) return 1;
  const perSlug = variantId ? ov.priceFactorPerSlug?.[variantId] : undefined;
  const perCat = ov.priceFactorPerCategorie ?? {};
  const catFactor = cats.map((c) => perCat[c]).find((f) => f != null);
  return clampFactor(perSlug ?? catFactor ?? ov.priceFactor);
}

/** Klantvriendelijke productnaam: de admin-override of anders de originele (Timmers-)naam. */
export function effectiveName(slug: string, original: string, ov: CatalogOverrides | null | undefined): string {
  const o = ov?.nameOverride?.[slug];
  return o && o.trim() ? o.trim() : original;
}

/** Getoond artikelnummer: de admin-override of anders het (eigen) standaardnummer. */
export function effectiveArticleNumber(slug: string, original: string, ov: CatalogOverrides | null | undefined): string {
  const o = ov?.articleNumberOverride?.[slug];
  return o && o.trim() ? o.trim() : original;
}

/**
 * Eigen ("logische") artikelnummer-code per top-categorie. Klantzijde tonen we niet het
 * Timmers-nummer maar een eigen GT-nummer: `GT-<CAT>-<NNN>`. De inkoop/bestelling bij
 * Timmers houdt altijd het originele nummer (staat in de order-notitie).
 */
const ARTICLE_CAT_CODE: Record<string, string> = {
  "vitrine-en-meubelbeslag": "VM",
  balustrade: "BA",
  "douche-en-bad": "DB",
  glasartikelen: "GA",
  interieurbeglazing: "IB",
  ventilatieroosters: "VR",
  // Configureerbaar glas (voor dossiers/toekomst)
  vacuumglas: "VG",
  isolatieglas: "IG",
  deuren: "DE",
};

/** 2–3-letterige categorie-code voor het eigen artikelnummer (met terugval uit de slug). */
export function articleCatCode(category: string): string {
  const known = ARTICLE_CAT_CODE[category];
  if (known) return known;
  const letters = category
    .split(/[^a-zA-Z]+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  return letters.slice(0, 2) || "XX";
}

/** Bouwt het eigen artikelnummer `GT-<CAT>-<NNN>` (index is 1-based binnen de categorie). */
export function autoArticleNumber(category: string, index1: number): string {
  return `GT-${articleCatCode(category)}-${String(index1).padStart(3, "0")}`;
}

/**
 * Klantvriendelijke basisnaam: strip een leidend Timmers-artikelnummer uit de naam
 * (bv. "013265-31 Marine glasklem…" → "Marine glasklem…"). Alleen het exacte nummer-prefix
 * of een leidende `NNNNNN-NN`-code wordt verwijderd; verder blijft de naam ongemoeid. De
 * originele naam gaat voor de inkoop mee in de order-notitie (createOrder).
 */
export function cleanArticleName(name: string, articleNumber: string | undefined): string {
  let n = (name ?? "").trim();
  const nr = (articleNumber ?? "").trim();
  if (nr && n.startsWith(nr)) {
    n = n.slice(nr.length).replace(/^[\s.:–—-]+/, "").trim();
  } else {
    // Leidende Timmers-code: cijfergroepen met '.' of '-' (bv. "013265-31 …" / "13.2105.070.18 …").
    // De code in de naam wijkt soms af van het opgeslagen artikelnummer, dus matchen we op vorm.
    // Alleen strippen als het er écht als een code uitziet (≥5 cijfers of ≥2 scheidingstekens),
    // zodat een gewone "2.1 meter …" niet per ongeluk wordt afgekapt.
    const m = n.match(/^(\d+(?:[.\-]\d+)+)\s+/);
    const code = m?.[1];
    if (m && code) {
      const digits = (code.match(/\d/g) ?? []).length;
      const seps = (code.match(/[.\-]/g) ?? []).length;
      if (digits >= 5 || seps >= 2) n = n.slice(m[0].length).trim();
    }
  }
  // Leverancier als merknaam vooraan ("Timmers Noodbeglazingsfolie 1000mm") gaat er ook af.
  // Wij verkopen dat product, niet Timmers — en bij wie wij inkopen hoort niet in een publieke
  // productnaam (zie [[publieke-data-hygiene]]). Dit werkt óók voor varianten, waar de admin
  // geen eigen naamveld voor heeft: die kwamen anders alsnog met "Timmers" ervoor op de pagina.
  n = n.replace(/^timmers\b[\s.:–—-]*/i, "").trim();
  return n || (name ?? "").trim();
}

/** Is een variant of soort uitgeschakeld in de overrides? */
export function isDisabled(id: string, ov: CatalogOverrides | null | undefined): boolean {
  return !!ov?.disabled?.includes(id);
}
